import React from 'react';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  InputAdornment,
  TextField,
  Autocomplete
} from '@mui/material';
import { CURRENCIES, Currency } from '../constants/currencies';
import { sharedClasses } from '../../../../../Components/CustomUIElements/sharedClasses';
import { theme } from '../../../../../../ThemeContext/ThemeObject';

interface CurrencyInputProps {
  value: string;
  currency: Currency;
  onChange: (value: string) => void;
  onCurrencyChange: (currency: Currency) => void;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
  error?: string;
  innerRef?: React.RefObject<HTMLInputElement> | ((el: HTMLDivElement) => void);
  placeholder?: string;
}

export default function CurrencyInput({
  value,
  currency,
  onChange,
  onCurrencyChange,
  label,
  fullWidth = false,
  required = false,
  error,
  innerRef,
  placeholder
}: CurrencyInputProps) {
  return (
    <FormControl
      fullWidth={fullWidth}
      sx={{ rowGap: 1, '.MuiFormLabel-asterisk': { color: theme.palette.error.main } }}
      required={required}
    >
      {label && (
        <FormLabel
          sx={{
            fontFamily: 'inherit',
            color: '#838383',
            '&.Mui-focused': {
              color: '#838383'
            }
          }}
        >
          {label}
        </FormLabel>
      )}
      <OutlinedInput
        id="currency-input"
        placeholder={placeholder || 'e.g. 60000'}
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        startAdornment={
          <InputAdornment position="start" sx={{ marginRight: 'unset' }}>
            <Autocomplete
              disablePortal
              options={CURRENCIES}
              getOptionLabel={(option) => option.code}
              value={currency}
              sx={{
                ...sharedClasses.formAutocomplete,
                '& .MuiOutlinedInput-root': {
                  border: 'unset',
                  width: '90px',
                  padding: 0.5,
                  fontFamily: 'Source Sans Pro'
                }
              }}
              slotProps={{
                paper: { style: { marginTop: '8px' } },
                listbox: { style: sharedClasses.autoCompleteListStyles }
              }}
              renderInput={(params) => (
                <TextField {...params} slotProps={{ inputLabel: { shrink: true } }} />
              )}
              onChange={(event, value) => onCurrencyChange(value)}
              disableClearable
            />
          </InputAdornment>
        }
        sx={{
          borderRadius: '6px',
          width: fullWidth ? '100%' : '48%',
          input: { padding: 1 },
          '&.MuiOutlinedInput-root': {
            paddingLeft: '6px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderWidth: '1px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)'
            }
          }
        }}
        ref={innerRef}
      />
      {error && (
        <FormHelperText
          sx={{
            color: theme.palette.error.main,
            margin: 'unset',
            marginLeft: 0.5,
            fontFamily: 'inherit'
          }}
        >
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
